import React, { useState, useEffect } from "react";
import Breadcrumb from "../../../components/Breadcrumb";
import Grid from "../../../components/Grid";
import CourseService from "../../../Api/CourseService";
import { useNavigate } from "react-router-dom";
import { toast } from 'react-toastify';
import Swal from 'sweetalert2/dist/sweetalert2.js';

const columns = [
    { 
      header: 'Title',
      key: 'title',
      render: (item) => (
        <div className="d-flex align-items-center">
          <img
            src={item.thumbnail_url || 'assets/images/default-course.png'}
            alt={item.title}
            className="w-40-px h-40-px rounded-circle flex-shrink-0 me-12 overflow-hidden"
          />
          <div className="flex-grow-1">
            <span className="text-md mb-0 fw-normal text-secondary-light">
              {item.title}
            </span>
          </div>
        </div>
      )
    },
    {
      header: 'Price',
      key: 'price',
      render: (item) => (
        <span>₹{item.price}</span>
      )
    },
    {
      header: 'Max Students',
      key: 'max_students'
    },
    {
      header: 'Validity (Days)',
      key: 'validity_days'
    },
    {
      header: 'Current Students',
      key: 'current_students'
    },
    {
      header: 'Status',
      key: 'is_sold_out',
      center: true,
      render: (item) => (
        <span className={`${!item.is_sold_out ? 'bg-success-focus text-success-600 border border-success-main' : 'bg-neutral-200 text-neutral-600 border border-neutral-400'} px-24 py-4 radius-4 fw-medium text-sm`}>
          {item.is_sold_out ? 'Sold Out' : 'Available'}
        </span>
      )
    }
];

const CourseListPage = () => {
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const courseAPI = new CourseService();

  // Add state for search and filter
  const [searchTitle, setSearchTitle] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const fetchCourses = async (title = "", status = "") => {
    try {
      setLoading(true);
      // Convert status filter to boolean for API
      let isSoldOut = undefined;
      if (status === 'Active') isSoldOut = false;
      if (status === 'Inactive') isSoldOut = true;
      
      const response = await courseAPI.GetAllCourse(title, isSoldOut);
      if (response.data && response.data.data) {
        setCourses(response.data.data);
        toast.success('Courses loaded successfully!');
      }
    } catch (err) {
      console.error("Error fetching courses:", err);
      toast.error("Failed to load courses. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  // Handle search change
  const handleSearch = (searchTerm) => {
    setSearchTitle(searchTerm);
    setCurrentPage(1);
    fetchCourses(searchTerm, statusFilter);
  };

  // Handle status filter change
  const handleStatusFilter = (status) => {
    setStatusFilter(status);
    setCurrentPage(1);
    fetchCourses(searchTitle, status);
  };

  // Handle items per page change
  const handleItemsPerPage = (perPage) => {
    setItemsPerPage(perPage);
    setCurrentPage(1);
  };

  useEffect(() => {
    fetchCourses();
  }, []);

  const handleEdit = (course) => {
    navigate(`/add-course?id=${course.id}`);
  };

  const handleView = (course) => {
    navigate(`/view-course?id=${course.id}`);
  };

  const handleDelete = async (course) => {
    try {
      const result = await Swal.fire({
        title: 'Are you sure?',
        text: "You won't be able to revert this!",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Yes, delete it!',
        showLoaderOnConfirm: true,
        preConfirm: async () => {
          try {
            const response = await courseAPI.DeleteById(course.id);
            if (response.data && response.data.status === "success") {
              return true;
            } else {
              throw new Error(response.data?.message || 'Failed to delete course');
            }
          } catch (error) {
            Swal.showValidationMessage(
              `Delete failed: ${error.response?.data?.message || error.message}`
            );
          }
        },
        allowOutsideClick: () => !Swal.isLoading()
      });

      if (result.isConfirmed) {
        await Swal.fire({
          title: 'Deleted!',
          text: 'Course has been deleted.',
          icon: 'success',
          timer: 1500,
          showConfirmButton: false
        });
        toast.success('Course deleted successfully!');
        fetchCourses(searchTitle, statusFilter); // Refresh with current filters
      }
    } catch (error) {
      console.error("Error in delete operation:", error);
      toast.error('An unexpected error occurred');
    }
  };

  if (loading) {
    return (
      <div className="text-center p-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <>
      <Breadcrumb title="Course List" />
      
      <Grid 
          data={courses}
          columns={columns}
          add="add-course"
          onEdit={handleEdit}
          onDelete={handleDelete}
          onView={handleView}
          onSearch={handleSearch}
          onStatusFilter={handleStatusFilter}
          onItemsPerPageChange={handleItemsPerPage}
          currentPage={currentPage}
          onPageChange={setCurrentPage}
          itemsPerPage={itemsPerPage}
        />
    </>
  );
};

export default CourseListPage; 
